import { Box, Stack, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import MarkdownRenderer from 'src/components/ui-component/markdown-renderer/MarkdownRenderer';
import { useApplication } from 'src/context/ApplicationContext';
import { ANTRAG_TYP, ANTRAGS_FORMULAREN, ART_DES_ANTRAGS, EINREICHUNG_DER_UNTERLAGEN } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { GetTextboxValidation, NonRequired } from 'src/lib/validation/ValidationTypes';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = Schema.attributes;

const AbschlussFoerderungBeantragen: FC<{ untertitel: string; inhalt: string }> = ({ untertitel, inhalt }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();

	return (
		<>
			<ApplicationHeading title={untertitel} />
			<MarkdownRenderer markdown={inhalt} />

			<UploadField />

			<CreateForm
				formStruc={formStrucHinweis(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={readOnly}
			/>

			<EinreichungHinweisLoeweSkizze />
			<PostVersandInformation application={application} />
		</>
	);
};

export default AbschlussFoerderungBeantragen;

const UploadField: FC<{}> = () => {
	const { application, setApplicationProperty, readOnly } = useApplication();

	const antragsFormular = get(application, P.Allgemein_Antragsformular.path, '');
	const antragsType = get(application, P.Allgemein_Typ.path, '');

	if (antragsFormular === ANTRAGS_FORMULAREN.Loewe && antragsType === ANTRAG_TYP.Skizze) {
		return (
			<CreateTable
				formStruc={formStrucUploadFieldLoeweSkizze(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={readOnly}
				size='small'
			/>
		);
	} else {
		return (
			<CreateForm
				formStruc={formStrucUploadField(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={readOnly}
			/>
		);
	}
};
const EinreichungHinweisLoeweSkizze: FC<{}> = () => {
	const { application } = useApplication();

	const antragsFormular = get(application, P.Allgemein_Antragsformular.path, '');
	const antragsType = get(application, P.Allgemein_Typ.path, '');

	if (antragsFormular === ANTRAGS_FORMULAREN.Loewe && antragsType === ANTRAG_TYP.Skizze) {
		return (
			<>
				<InhaltTrenner dotted />
				<InhaltWrapper>
					<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
						Form- und fristgerechte Einreichung der Unterlagen
					</Typography>

					<Typography>
						<strong>Bitte beachten Sie:</strong> Für die form- und fristgerechte Einreichung muss die unterzeichnete Skizze mit allen Anlagen
						(vollständig ausgefüllt und eigenhändig unterzeichnet) vier Wochen vor einer Sitzung der LOEWE-3-Auswahlkommission im Original bei
						uns im Haus vorliegen müssen.{' '}
					</Typography>

					<Typography>
						Mit einem "Klick" auf den Button unten rechts können Sie anschließend Ihre Skizze (als Vorab-Information) bei uns einreichen.
					</Typography>
				</InhaltWrapper>
			</>
		);
	} else {
		return <> </>;
	}
};

const formStrucUploadField = (application: any) => {
	const antragsType = get(application, P.Allgemein_Typ.path, '');
	const artDesAntrags = get(application, P.Allgemein_Art_Des_Antrags.path, '');
	const isFormantrag = artDesAntrags === ART_DES_ANTRAGS.Formantrag;
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Allgemein_Eingereichter_Antrag_Skizze,
					maxFileSize: 10000000,
					hideLabel: true,
					allowedFileNameKeywords:
						antragsType === ANTRAG_TYP.Skizze ? ['Skizze']
						: isFormantrag ? ['Formantrag']
						: ['Antrag'],
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
	return struc;
};

const formStrucUploadFieldLoeweSkizze = (application: any) => {
	const antragsType = get(application, P.Allgemein_Typ.path, '');

	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: 't.t',
					type: 'label',
					label: '1. Skizze - aktueller Status, systemgeneriertes PDF',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Allgemein_Generiertes_PDF,
					maxFileSize: 10000000,
					hideLabel: true,
					allowedFileNameKeywords: antragsType === ANTRAG_TYP.Skizze ? ['Skizze'] : ['Antrag'],
				},
			],
		},
		{
			fields: [
				{ path: 't.t', type: 'label', label: '2. Skizze - Upload (unterzeichnet)', validation: [{ validateFunction: NonRequired }] },
				{
					...P.Allgemein_Eingereichter_Antrag_Skizze,
					maxFileSize: 10000000,
					hideLabel: true,
					allowedFileNameKeywords: antragsType === ANTRAG_TYP.Skizze ? ['Skizze'] : ['Antrag'],
				},
			],
		},
	];
	return struc;
};

const formStrucHinweis = (application: any) => {
	const antragsType = get(application, P.Allgemein_Typ.path, '');
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Allgemein_Hinweise_An_Die_HA,
					label:
						antragsType === ANTRAG_TYP.Skizze ?
							'Upload Skizze - Ihre Nachricht an die Hessen Agentur (optional):'
						:	'Upload Förderantrag - Ihre Nachricht an die Hessen Agentur (optional):',
					maxCharLength: 2000,
					validation: [{ validateFunction: GetTextboxValidation(2000), onlyValidateOnNonEmptyValue: true }],
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];
	return struc;
};

const PostVersandInformation: FC<{ application: any }> = ({ application }) => {
	const antragsType = get(application, P.Allgemein_Typ.path, '');
	const antragsformular = get(application, P.Allgemein_Antragsformular.path, '');
	if (antragsformular === ANTRAGS_FORMULAREN.Loewe && antragsType === ANTRAG_TYP.Antrag) {
		return <></>;
	}

	const EinreichungDerUnterlagen = get(application, P.Foerderprogramm.schema.attributes.Konfiguration_Einreichung_Der_Antragsunterlagen.path, '');
	if (
		EinreichungDerUnterlagen === EINREICHUNG_DER_UNTERLAGEN.postalisch &&
		antragsformular !== ANTRAGS_FORMULAREN.StartUpProjekte &&
		(antragsformular !== ANTRAGS_FORMULAREN.Loewe || antragsType !== ANTRAG_TYP.Skizze)
	) {
		return (
			<>
				<Stack spacing={3} sx={{ mt: '1rem' }}>
					<InhaltTrenner />
					<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
						{antragsType === ANTRAG_TYP.Skizze ? 'Einreichung der Skizze-Unterlagen' : 'Einreichung der Antragsunterlagen'}
					</Typography>
					<Typography>
						Bitte beachten Sie, dass für die fristgerechte {antragsType === ANTRAG_TYP.Skizze ? 'Skizze-Einreichung' : 'Antragseinreichung'}{' '}
						alle Antragsdokumente (vollständig ausgefüllt und eigenhändig unterzeichnet) im Original bei uns im Haus vorliegen müssen.
					</Typography>
					<Box sx={{ mb: '5rem !important' }}>{/*Platzhalter element*/}</Box>
				</Stack>
			</>
		);
	}
	return <></>;
};

export const getFormStrucAbschlussFoerderungBeantragen = (application: any) => {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path, '');
	const antragsType = get(application, P.Allgemein_Typ.path, '');

	if (antragsFormular === ANTRAGS_FORMULAREN.Loewe && antragsType === ANTRAG_TYP.Skizze) {
		return concat(formStrucUploadFieldLoeweSkizze(application), formStrucHinweis(application));
	} else {
		return concat(formStrucUploadField(application), formStrucHinweis(application));
	}
};
